import { useState, useCallback } from 'react';
import { Check, Droplets, FlaskConical } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { ErlenmeyerFlask } from '@/components/lab';

const stepInfoContent: Record<number, { title: string; content: string; formulas?: string[] }> = {
  1: {
    title: 'Quantitative Transfer',
    content: 'Transfer all of the crushed antacid powder to the flask. Use a spatula and brush to collect all material from the mortar. Rinse the mortar with small portions of distilled water to ensure complete transfer. Any powder left behind reduces accuracy.',
    formulas: ['% Transfer = (mass transferred / mass weighed) x 100']
  },
  2: {
    title: 'Adding Distilled Water',
    content: 'Adding 50 mL of distilled water creates a suspension of the antacid powder. Use a graduated cylinder for approximate volume - exact volume is not critical here. The water helps disperse the powder and prepares for indicator addition.',
    formulas: ['Volume water = 50 mL (approximate)']
  },
  3: {
    title: 'Methyl Orange Indicator',
    content: 'Add 10 drops of methyl orange indicator (0.1% in ethanol), then swirl gently to mix. Methyl orange changes from yellow (pH > 4.4) to red (pH < 3.1) through an orange transition. With basic antacid present, the solution appears yellow-orange. During HCl titration, the color will shift to pink-red at the endpoint.',
    formulas: ['pKa methyl orange = 3.7', 'Yellow (basic) <-> Orange <-> Red (acidic)']
  }
};

// Methyl Orange indicator colors
const INDICATOR_COLORS = {
  basic: '#FFD700', // Yellow (pH > 4.4)
  transition1: '#FFA500', // Yellow-Orange
  transition2: '#FF8C00', // Orange
  transition3: '#FF6347', // Red-Orange
  endpoint: '#FF4500' // Red (pH < 3.1)
};

export default function Part2SamplePrep() {
  const [powderTransferred, setPowderTransferred] = useState(false);
  const [waterAdded, setWaterAdded] = useState(false);
  const [indicatorAdded, setIndicatorAdded] = useState(false);
  const [fillLevel, setFillLevel] = useState(0);
  const [solutionColor, setSolutionColor] = useState('#FFFFFF');
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const completedSteps = [powderTransferred, waterAdded, indicatorAdded].filter(Boolean).length;

  const handleTransferPowder = useCallback(() => {
    if (!powderTransferred) {
      setPowderTransferred(true);
      setFillLevel(5);
      setSolutionColor('#F5F5DC');
      setCurrentStep(2);
    }
  }, [powderTransferred]);

  const handleAddWater = useCallback(() => {
    if (powderTransferred && !waterAdded) {
      setWaterAdded(true);
      setFillLevel(50);
      setSolutionColor('#FFFFF0'); // Pale yellow suspension
      setCurrentStep(3);
    }
  }, [powderTransferred, waterAdded]);

  const handleAddIndicator = useCallback(() => {
    if (waterAdded && !indicatorAdded) {
      setIndicatorAdded(true);
      // Antacid is basic, so methyl orange shows yellow
      setSolutionColor(INDICATOR_COLORS.basic);
    }
  }, [waterAdded, indicatorAdded]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part II: Sample Preparation</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Transfer to Flask', hint: 'Add crushed antacid powder', done: powderTransferred },
            { id: 2, title: 'Add Water', hint: 'Add 50 mL distilled water', done: waterAdded },
            { id: 3, title: 'Add Indicator', hint: 'Add methyl orange drops', done: indicatorAdded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Indicator Color Reference */}
        <div className="color-reference">
          <h4>Methyl Orange Colors</h4>
          <div className="color-scale">
            <div className="color-item">
              <div className="color-swatch" style={{ background: INDICATOR_COLORS.basic }} />
              <span>Basic (pH {'>'} 4.4)</span>
            </div>
            <div className="color-item">
              <div className="color-swatch" style={{ background: INDICATOR_COLORS.transition2 }} />
              <span>Transition</span>
            </div>
            <div className="color-item">
              <div className="color-swatch" style={{ background: INDICATOR_COLORS.endpoint }} />
              <span>Acidic (pH {'<'} 3.1)</span>
            </div>
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Sample Preparation"
        completedCount={completedSteps}
        totalSteps={3}
        equipment={['250 mL Erlenmeyer flask', 'Distilled water', 'Methyl orange indicator']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="prep-workspace">
          <div className="equipment-row">
            {/* Powder Container */}
            <div
              className={`equipment-station ${currentStep === 1 && !powderTransferred ? 'clickable' : ''}`}
              onClick={handleTransferPowder}
              style={{ cursor: currentStep === 1 && !powderTransferred ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 60 80" width={60} height={80}>
                <rect x="15" y="20" width="30" height="50" rx="3" fill="#5A5A5A" />
                <rect x="18" y="25" width="24" height="40" fill={powderTransferred ? '#E8E8E8' : '#F5F5DC'} opacity="0.9" />
                <rect x="18" y="10" width="24" height="12" rx="2" fill="#333" />
                <text x="30" y="50" fontSize="6" fill="#333" textAnchor="middle">Antacid</text>
                <text x="30" y="58" fontSize="5" fill="#333" textAnchor="middle">Powder</text>
              </svg>
              <span className="equipment-label">Crushed Antacid</span>
            </div>

            {/* Erlenmeyer Flask */}
            <div
              className={`equipment-station ${(currentStep === 2 || currentStep === 3) && !indicatorAdded ? 'clickable' : ''}`}
              onClick={() => {
                if (currentStep === 2) handleAddWater();
                else if (currentStep === 3) handleAddIndicator();
              }}
              style={{ cursor: (currentStep === 2 || currentStep === 3) && !indicatorAdded ? 'pointer' : 'default' }}
            >
              <ErlenmeyerFlask
                size={250}
                fillLevel={fillLevel}
                fillColor={solutionColor}
                hasStirBar={false}
                stirring={false}
                active={(currentStep === 2 && !waterAdded) || (currentStep === 3 && !indicatorAdded)}
              />
              <span className="equipment-label">250 mL Erlenmeyer Flask</span>
            </div>

            {/* Water Bottle */}
            <div className="equipment-station">
              <svg viewBox="0 0 50 90" width={60} height={100}>
                <rect x="10" y="25" width="30" height="55" rx="3" fill="#5A5A5A" />
                <rect x="13" y="30" width="24" height="45" fill="#E3F2FD" opacity="0.8" />
                <rect x="15" y="10" width="20" height="15" rx="2" fill="#333" />
                <text x="25" y="60" fontSize="5" fill="#333" textAnchor="middle">DI</text>
                <text x="25" y="68" fontSize="5" fill="#333" textAnchor="middle">Water</text>
              </svg>
              <span className="equipment-label">Distilled Water</span>
            </div>

            {/* Indicator Bottle */}
            <div className="equipment-station">
              <svg viewBox="0 0 40 80" width={50} height={90}>
                <rect x="10" y="25" width="20" height="45" rx="2" fill="#4A4A4A" />
                <rect x="12" y="30" width="16" height="35" fill={INDICATOR_COLORS.transition2} opacity="0.8" />
                <rect x="12" y="10" width="16" height="15" rx="1" fill="#333" />
                <circle cx="20" cy="73" r="2" fill={INDICATOR_COLORS.transition2} />
                <text x="20" y="50" fontSize="4" fill="#FFF" textAnchor="middle">MO</text>
              </svg>
              <span className="equipment-label">Methyl Orange</span>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {!powderTransferred && (
              <div className="status-message info">
                <Droplets size={20} />
                Click the powder container to transfer antacid to the flask
              </div>
            )}
            {powderTransferred && !waterAdded && (
              <div className="status-message info">
                <Droplets size={20} />
                Click the flask to add 50 mL of distilled water
              </div>
            )}
            {waterAdded && !indicatorAdded && (
              <div className="status-message info">
                <Droplets size={20} />
                Click the flask to add 10 drops of methyl orange indicator, then swirl gently
              </div>
            )}
            {indicatorAdded && (
              <div className="status-message success">
                <Check size={20} />
                Sample preparation complete! Solution is yellow (basic). Ready for burette setup.
              </div>
            )}
          </div>

          {/* Solution appearance indicator */}
          {indicatorAdded && (
            <div className="solution-indicator">
              <div className="indicator-display" style={{ background: solutionColor }}>
                <span>Current Color: Yellow (Basic)</span>
              </div>
            </div>
          )}

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Methyl Orange Indicator</h4>
            <p>
              Methyl orange is a pH indicator that changes from yellow (pH {'>'} 4.4) to red (pH {'<'} 3.1).
              In the presence of the basic antacid, the solution appears yellow.
            </p>
            <p>
              During titration, as HCl is added, the pH will decrease and the color will
              transition through orange to red at the endpoint.
            </p>
          </div>
        </div>

        <style>{`
          .prep-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 40px;
            align-items: flex-end;
            justify-content: center;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .equipment-label {
            font-size: 12px;
            color: var(--text-secondary);
            text-align: center;
          }

          .color-reference {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 16px;
          }

          .color-reference h4 {
            margin: 0 0 10px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .color-scale {
            display: flex;
            flex-direction: column;
            gap: 6px;
          }

          .color-item {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .color-swatch {
            width: 20px;
            height: 20px;
            border-radius: 4px;
            border: 1px solid rgba(0,0,0,0.2);
          }

          .color-item span {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .solution-indicator {
            margin-top: 10px;
          }

          .indicator-display {
            padding: 10px 20px;
            border-radius: 8px;
            text-align: center;
            border: 2px solid rgba(0,0,0,0.1);
          }

          .indicator-display span {
            color: #333;
            font-weight: 600;
            font-size: 14px;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .theory-box p:last-child {
            margin-bottom: 0;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp10"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
